import api from './client';
import type {
  Settings,
  Category,
  Product,
  ProductListItem,
  Customer,
  Seller,
  Role,
  Order,
  OrderListItem,
  SaleInput,
  SaleResult,
  PaginatedResponse,
  BulkUploadResult,
  Expense,
  ExpenseSummary,
  IncomeSummary,
  PaymentRow,
  CustomerLedger
} from '../types';

// Auth
export const authApi = {
  login: (username: string, password: string) =>
    api.post('/auth/login/', { username, password }),
  logout: () => api.post('/auth/logout/'),
  getUser: () => api.get('/auth/user/'),
};

// Settings — backend kimligingizga qarab do'kon yoki platforma sozlamasini beradi
export const settingsApi = {
  get: () => api.get<Settings>('/settings/'),
  update: (data: Partial<Settings>) => api.put<Settings>('/settings/', data),
};

// Telegram akkauntini ulash (Mini App sotuvchini shu orqali taniydi)
export const telegramLinkApi = {
  status: () => api.get<{ linked: boolean; telegram_id: number | null }>('/auth/telegram-link/'),
  create: () => api.post<{ link: string; expires_in_minutes: number }>('/auth/telegram-link/'),
  unlink: () => api.delete('/auth/telegram-link/'),
};

// Categories
export const categoriesApi = {
  list: (params?: Record<string, any>) =>
    api.get<Category[]>('/categories/', { params }),
  tree: (params?: Record<string, any>) =>
    api.get<PaginatedResponse<Category>>('/categories/tree/', { params }),
  get: (id: number) => api.get<Category>(`/categories/${id}/`),
  create: (data: Partial<Category>) => api.post<Category>('/categories/', data),
  update: (id: number, data: Partial<Category>) => api.put<Category>(`/categories/${id}/`, data),
  delete: (id: number) => api.delete(`/categories/${id}/`),
};

// Products
export const productsApi = {
  list: (params?: Record<string, any>) =>
    api.get<PaginatedResponse<ProductListItem>>('/products/', { params }),
  search: (q: string) => api.get<ProductListItem[]>('/products/search/', { params: { q } }),
  /** Sotuv ekrani uchun butun katalog — bitta so'rovda. */
  pos: () => api.get<{ results: ProductListItem[]; truncated: boolean }>('/products/pos/'),
  get: (id: number) => api.get<Product>(`/products/${id}/`),
  create: (data: Partial<Product>) => api.post<Product>('/products/', data),
  update: (id: number, data: Partial<Product>) => api.put<Product>(`/products/${id}/`, data),
  delete: (id: number) => api.delete(`/products/${id}/`),
  updateStock: (id: number, quantity: number, action: 'add' | 'set' = 'set') =>
    api.post(`/products/${id}/update_stock/`, { quantity, action }),
  lowStock: () => api.get<ProductListItem[]>('/products/low_stock/'),
  nextCodes: () => api.get<{ sku: string; barcode: string }>('/products/next_codes/'),
  bulkTemplate: () =>
    api.get('/products/bulk_template/', { responseType: 'blob', timeout: 120000 }),
  bulkUpload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<BulkUploadResult>('/products/bulk_upload/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
};

// Customers
export const customersApi = {
  list: (params?: Record<string, any>) =>
    api.get<PaginatedResponse<Customer>>('/customers/', { params }),
  get: (id: number) => api.get<Customer>(`/customers/${id}/`),
  getByPhone: (phone: string) => api.get<Customer>('/customers/by_phone/', { params: { phone } }),
  withDebt: () => api.get<Customer[]>('/customers/with_debt/'),
  ledger: (id: number) => api.get<CustomerLedger>(`/customers/${id}/ledger/`),
  /** Mijozga yuboriladigan ochiq hisob havolasi (bor bo'lsa o'sha qaytadi). */
  statementLink: (id: number) =>
    api.post<StatementLink>(`/customers/${id}/statement_link/`),
  /** Havolani o'chirish — keyingi yuborishda yangisi yaratiladi. */
  revokeStatementLink: (id: number) => api.delete(`/customers/${id}/statement_link/`),
  payDebt: (id: number, data: { amount: number; method: string }) =>
    api.post<{ paid: string; remaining_debt: string; customer: Customer }>(
      `/customers/${id}/pay_debt/`, data,
    ),
  create: (data: Partial<Customer>) => api.post<Customer>('/customers/', data),
  update: (id: number, data: Partial<Customer>) => api.put<Customer>(`/customers/${id}/`, data),
  delete: (id: number) => api.delete(`/customers/${id}/`),
};

export interface StatementLink {
  url: string;
  /** Havolasiz xabar — Telegram ulashish oynasi havolani o'zi qo'shadi. */
  message: string;
  /** Xabar + havola, SMS va boshqa ilovalar uchun. */
  text: string;
}

// Sellers
export const sellersApi = {
  list: () => api.get<Seller[]>('/sellers/'),
  get: (id: number) => api.get<Seller>(`/sellers/${id}/`),
  create: (data: Record<string, any>) => api.post<Seller>('/sellers/', data),
  update: (id: number, data: Record<string, any>) => api.put<Seller>(`/sellers/${id}/`, data),
  delete: (id: number) => api.delete(`/sellers/${id}/`),
};

// Ruxsatlar katalogi — rol matritsasi shundan chiziladi.
export interface PermissionAction {
  code: string;
  label: string;
  permission: string;
}

export interface PermissionResource {
  code: string;
  label: string;
  scopes: Array<'own' | 'shop'>;
  nav_permission: string;
  actions: PermissionAction[];
}

export interface PermissionCatalog {
  scopes: Array<{ code: 'own' | 'shop'; label: string }>;
  resources: PermissionResource[];
}

export const permissionsApi = {
  catalog: () => api.get<PermissionCatalog>('/permissions/'),
};

// Roles (RBAC)
export const rolesApi = {
  list: () => api.get<Role[]>('/roles/'),
  get: (id: number) => api.get<Role>(`/roles/${id}/`),
  create: (data: Partial<Role>) => api.post<Role>('/roles/', data),
  update: (id: number, data: Partial<Role>) => api.put<Role>(`/roles/${id}/`, data),
  delete: (id: number) => api.delete(`/roles/${id}/`),
};

// Kassa chiqimlari (ish xarajati va avans)
export const expensesApi = {
  list: (params?: Record<string, any>) =>
    api.get<PaginatedResponse<Expense> | Expense[]>('/expenses/', { params }),
  create: (data: Record<string, any>) => api.post<Expense>('/expenses/', data),
  delete: (id: number) => api.delete(`/expenses/${id}/`),
  summary: () => api.get<ExpenseSummary>('/expenses/summary/'),
};

// Kirimlar — kassaga tushgan to'lovlar
export const paymentsApi = {
  list: (params?: Record<string, any>) =>
    api.get<PaginatedResponse<PaymentRow>>('/payments/', { params }),
  summary: () => api.get<IncomeSummary>('/payments/summary/'),
};

// Orders
export const ordersApi = {
  list: (params?: Record<string, any>) =>
    api.get<PaginatedResponse<OrderListItem>>('/orders/', { params }),
  /**
   * Sotuv yaratish. Sotuvchi yuborilmaydi — server uni so'rovdan oladi,
   * shuning uchun bu chaqiruv web'da ham, Mini App'da ham bir xil.
   */
  create: (data: SaleInput) => api.post<SaleResult>('/orders/', data),
  get: (id: number) => api.get<Order>(`/orders/${id}/`),
  receipt: (id: number) => api.get(`/orders/${id}/receipt/`),
  receiptPdf: (id: number) =>
    api.get(`/orders/${id}/receipt/pdf/`, { responseType: 'blob', timeout: 120000 }),
  /** Chop etish sahifasining imzolangan havolasi (1 soat amal qiladi). */
  printLink: (id: number) =>
    api.post<{ success: boolean; url: string }>(`/orders/${id}/print_link/`),
  /** Chek PDF'ini Telegram'da ulashish uchun tayyor xabar. */
  shareTelegram: (id: number) =>
    api.post<{ success: boolean; prepared_message_id: string }>(
      `/orders/${id}/share_telegram/`,
    ),
  addPayment: (id: number, data: { amount: number; method: string }) =>
    api.post<Order>(`/orders/${id}/add_payment/`, data),
  cancel: (id: number) => api.post<Order>(`/orders/${id}/cancel/`),
  edit: (
    id: number,
    data: {
      due_date?: string | null;
      notes?: string;
      customer?: number | null;
      seller?: number | null;
    },
  ) =>
    api.patch<Order>(`/orders/${id}/edit/`, data),
};
